<template>
    <header ref="headerRef" class="header-unlogged" :class="{'header-unlogged--collapsed': isCollapsed}">
        <div v-if="hasMultipleModes" ref="topnavRef" class="header-unlogged__topnav">
            <NavbarTop />
        </div>
        <Navbar />
    </header>
</template>
<script setup lang="ts">
    import {ref, useTemplateRef, onMounted, onBeforeUnmount} from 'vue';
    import Navbar from './Navbar.vue';
    import NavbarTop from './NavbarTop.vue';
    import {useHeaderUnloggedScroll} from '../composables/useHeaderUnloggedScroll';
    import {useNavbarMode} from '../composables/useNavbarMode.ts';

    const {hasMultipleModes} = useNavbarMode();
    const {isCollapsed} = useHeaderUnloggedScroll();

    const resizeTimer = ref<ReturnType<typeof setTimeout> | undefined>(undefined);
    function onResize() {
        document.querySelector('header')?.classList.add('resize-animation-stopper');

        clearTimeout(resizeTimer.value);
        resizeTimer.value = setTimeout(() => {
            document.querySelector('header')?.classList.remove('resize-animation-stopper');
        }, 400);
    }

    const headerRef = useTemplateRef('headerRef');
    const topnavRef = useTemplateRef('topnavRef');

    /* On mobile the topnav is out of flow, so its height cannot be read from the layout anymore. */
    function publishTopnavHeight() {
        const topnavContent = topnavRef.value?.firstElementChild;
        if (!(topnavContent instanceof HTMLElement)) {
            return;
        }

        headerRef.value?.style.setProperty('--topnav-height', `${topnavContent.offsetHeight}px`);
    }

    onMounted(() => {
        window.addEventListener('resize', onResize);
        publishTopnavHeight();
    });

    onBeforeUnmount(() => {
        window.removeEventListener('resize', onResize);
    });
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .resize-animation-stopper * {
        transition: none !important;
    }

    .header-unlogged {
        --navbar-transition-duration: 0.6s;
        --navbar-transition-easing: cubic-bezier(0.4, 0, 0.2, 1);
        --topnav-height: 0px;

        /* room the menu overlay has to leave for the topnav, 0 unless the topnav is expanded */
        --topnav-offset: 0px;
        --navbar-height: 60px;

        position: sticky;
        top: 0;
        z-index: 1000;
        background-color: var(--joy-color-background-default);
        padding: 0 var(--joy-core-spacing-6);
        width: 100%;
        transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        transform: translateY(0);
        font-size: var(--joy-font-size-primary-300);
        display: flex;
        flex-direction: column-reverse;

        /* the header stays in flow, so its box must never resize: any height change relayouts the page */
        height: var(--navbar-height);

        &__topnav {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            z-index: 2;
            height: 0;
            overflow: hidden;
            background-color: var(--joy-color-background-default);
            transition: height var(--joy-transition-duration-long) var(--joy-transition-timing-function);
        }

        &:has(.navbar__content--open):not(:has(.navbar__submenu--open)) {
            --topnav-offset: var(--topnav-height);

            .header-unlogged__topnav {
                height: var(--topnav-height);
            }
        }
    }

    /* desktop */
    @include mq.screen_md(min) {
        .header-unlogged {
            flex-direction: column;
            padding: 0 var(--joy-core-spacing-7);
            border-bottom: 1px solid var(--joy-color-neutral-30);

            &:has(.header-unlogged__topnav) {
                --topnav-height: 50px;
            }

            &--collapsed:not(:focus-within) {
                transform: translateY(calc(-1 * var(--topnav-height)));
            }
        }

        .header-unlogged,
        .header-unlogged:has(.navbar__content--open):not(:has(.navbar__submenu--open)) {
            height: auto;

            .header-unlogged__topnav {
                position: static;
                height: auto;
                overflow: visible;
                transition: none;
            }
        }
    }
</style>
